import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Conversation} from '../../../models/Conversation';
import {ReadStatus} from '../../../enums/read-status';
import {UpdateConversationReadStatus, MarkConversationsAsUnread} from '../../state/actions';
import { select, Store} from '@ngrx/store';
import {AppState} from '../../../state/reducers';
import {Router, ActivatedRoute} from "@angular/router";
import { interval, Observable, takeWhile, Subscription } from 'rxjs';
import { isConversationPreviewEnabled, isEmbedMode } from '../../../state/selectors';
import { GetViewers } from '../../../viewers/state/actions';
import { ViewerType } from '../../../enums/viewers-type';
import { Practice } from "../../../models/Practice";
import {
  getCurrentPractice,
} from "../../../practices/state/selectors";
import { practiceHasFeature } from '../../../helpers/practice-has-feature';
import { PracticeFeature } from '../../../enums/practice-feature';
import { PostMessageService } from '../../../services/post-message.service';
import { PostMessageAction } from '../../../enums/post-message-action';
import { Checkbox } from 'primeng/checkbox';

@Component({
  selector: 'conversation-list-item',
  templateUrl: './conversation-list-item.component.html',
  styleUrls: ['./conversation-list-item.component.scss']
})
export class ConversationListItem implements OnInit, OnDestroy {
  @Output() selectedToggled = new EventEmitter();
  @Output() clicked = new EventEmitter<Conversation>();
  @Input() conversation?: Conversation;
  @Input() selectable = false;
  @Input() selected = false;
  @Input() useLastResponseDate = false;
  @Input() showBorder = false;
  @Input() shouldReset = false;
  readStatus = ReadStatus;
  actionsOpen = false;
  refresh = new Date();
  alive = true;
  previewEnabled: boolean = true;
  practice$?: Observable<Practice | null>;
  practiceSub?: Subscription;
  practice?: Practice;
  clientInsight = false;
  embedMode = false;
  @ViewChild('checkbox') checkbox?: Checkbox;
  constructor(private store: Store<AppState>, private router: Router, private route: ActivatedRoute, private postMessageService: PostMessageService) {
    this.store.pipe(select(isConversationPreviewEnabled)).pipe(takeWhile(() => this.alive)).subscribe(enabled => this.previewEnabled = enabled);
    this.store.pipe(select(isEmbedMode)).pipe(takeWhile(() => this.alive)).subscribe(enabled => this.embedMode = enabled);
  }

  ngOnInit(): void {
    interval(30000).pipe(takeWhile(() => this.alive)).subscribe(() => {
      this.refresh = new Date();
    });
    this.subscribeToCurrentPractice();

    const feature = this.practice?.features.find(f => f.slug === 'client-insights-page');

    if (feature) {
      this.clientInsight = feature.enabled;
    }
  }

  ngOnDestroy(): void {
    this.alive = false;
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['shouldReset'] && changes['shouldReset'].currentValue === true && this.selected) {
      this.selected = false;
      if (this.checkbox) {
        this.checkbox.writeValue(false);
      }
    }
  }
  selectedChanged(): void {
    if (this.conversation && !this.shouldReset) {
      this.selectedToggled.emit();
    }
  }

  toggleActions(): void {
    this.actionsOpen = !this.actionsOpen;
  }

  markRead(): void {
    this.actionsOpen = false;
    if (this.conversation) {
      this.store.dispatch(UpdateConversationReadStatus({conversations: [this.conversation]}));
    }
  }


  markUnread(): void {
    this.actionsOpen = false;
    if (this.conversation) {
      this.store.dispatch(MarkConversationsAsUnread({conversations: [this.conversation]}));
    }
  }

  redirectToClient(clientId: string): void {
    if (!this.embedMode) {
      this.router.navigateByUrl(`/clients/${clientId}`);
    }
  }

  conversationClicked(e: any): void {
    if (
      this.clientInsight &&
      e.target.id === 'client' &&
      this.conversation &&
      this.conversation.client
    ) {
      if (this.embedMode) {
        this.navigateToConversation();
      } else {
        this.redirectToClient(this.conversation.client.id)
      }
    } else {
      if (this.previewEnabled) {
        if (this.conversation) {
          this.clicked.emit(this.conversation);
        }
        return;
      }
      this.navigateToConversation();
    }
  }

  navigateToConversation(): void {
    if (!this.conversation) return;

    if (!this.embedMode) {
      const currentQueryParams = this.route.snapshot.queryParams;
      this.router.navigate(['/conversations', this.conversation.id], {
        queryParams: currentQueryParams
      });
    } else {
      if (this.conversation.client) {
        this.postMessageService.postMessage(PostMessageAction.NAVIGATE, {
          page: 'client',
          clientId: this.conversation.client?.pmsId
        });
        return;
      }
      
      const urlTree = this.router.parseUrl(this.router.url);

      const segments = urlTree.root.children['primary']?.segments.map(s => s.path) || [];
      const updatedSegments = segments.map(segment => segment === 'day-list' ? 'chat' : segment);

      const queryParams = {
        ...urlTree.queryParams,
        conversationId: this.conversation.id
      };

      const newUrlTree = this.router.createUrlTree(updatedSegments, { queryParams });

      this.router.navigateByUrl(newUrlTree);
    }
  }

  subscribeToCurrentPractice(): void {
    this.practice$ = this.store
      .pipe(select(getCurrentPractice))
      .pipe(takeWhile(() => this.alive));

    this.practiceSub = this.practice$.subscribe((practice) => {
      if (practice) {
        this.practice = practice;
        this.updateEnabled();
      }
    });
  }
  formatStatusKey(statusName?: string): string {
    if (!statusName) return 'UNKNOWN_STATUS'; // Default fallback
    return statusName.toUpperCase().replace(/\s+/g, '_'); // Converts "With Client" → "WITH_CLIENT"
  }
  updateEnabled(): void {
    this.clientInsight = practiceHasFeature(this.practice, PracticeFeature.CLIENT_INSIGHTS_PAGE);
  }

}
